import {useCallback, useEffect, useMemo, useRef} from 'react';
import {debounce} from 'ts-debounce';

export enum ScrollSyncDirection {
  Vertical,
  Horizontal,
  Both,
}
const VerticalScrollDirections = [ScrollSyncDirection.Vertical, ScrollSyncDirection.Both];
const HorizontalScrollDirections = [ScrollSyncDirection.Horizontal, ScrollSyncDirection.Both];

function syncScroll(
  event: UIEvent,
  element: Element | null | undefined,
  scrollSyncDirection: ScrollSyncDirection = ScrollSyncDirection.Both
): void {
  if (!element) {
    return;
  }
  if (VerticalScrollDirections.includes(scrollSyncDirection)) {
    element.scrollTop = (event.currentTarget as HTMLElement).scrollTop;
  }
  if (HorizontalScrollDirections.includes(scrollSyncDirection)) {
    element.scrollLeft = (event.currentTarget as HTMLElement).scrollLeft;
  }
}

export const SCROLL_OVERLAP_TIMEOUT = 300;

/** Make two elements scroll in sync */
export default function useScrollSync(
  element1: Element | null | undefined,
  element2: Element | null | undefined,
  scrollSyncDirection: ScrollSyncDirection = ScrollSyncDirection.Both
): void {
  const isElement1Scrolling = useRef(false);
  const isElement2Scrolling = useRef(false);

  const endElement1Scroll = useMemo(
    () =>
      debounce(() => {
        isElement1Scrolling.current = false;
      }, SCROLL_OVERLAP_TIMEOUT),
    []
  );

  const endElement2Scroll = useMemo(
    () =>
      debounce(() => {
        isElement2Scrolling.current = false;
      }, SCROLL_OVERLAP_TIMEOUT),
    []
  );

  const element1OnScroll = useCallback(
    (event) => {
      if (isElement2Scrolling.current) {
        return;
      }
      isElement1Scrolling.current = true;
      syncScroll(event, element2, scrollSyncDirection);
      endElement1Scroll();
    },
    [element2, endElement1Scroll, scrollSyncDirection]
  );
  const element2OnScroll = useCallback(
    (event) => {
      if (isElement1Scrolling.current) {
        return;
      }
      isElement2Scrolling.current = true;
      syncScroll(event, element1, scrollSyncDirection);
      endElement2Scroll();
    },
    [element1, endElement2Scroll, scrollSyncDirection]
  );

  useEffect(() => {
    element1?.addEventListener('scroll', element1OnScroll);
    element2?.addEventListener('scroll', element2OnScroll);
    return () => {
      element1?.removeEventListener('scroll', element1OnScroll);
      element2?.removeEventListener('scroll', element2OnScroll);
    };
  }, [element1, element1OnScroll, element2, element2OnScroll]);
}
